// MessengerUtils.js
// Helper utility functions for the messenger component
import { therapistClient } from '../../../../PatientApps/apiClient';

/**
 * Converts a string to a deterministic color value
 * @param {string} string - Input string to generate color from
 * @returns {string} - Hex color code
 */
export function stringToColor(string) {
  if (!string) return '#6e7582'; // Default color for empty strings
  
  let hash = 0;
  let i;
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

/**
 * Gets initials from a name
 * @param {string} name - Full name
 * @returns {string} - Initials (uppercase)
 */
export function getInitials(name) {
  if (!name) return '';
  
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

/**
 * Formats a timestamp to a readable time
 * @param {Date|string} timestamp - Date object or ISO string
 * @returns {string} - Formatted time string
 */
export function formatTime(timestamp) {
  if (!timestamp) return '';
  
  const date = typeof timestamp === 'string' 
    ? new Date(timestamp) 
    : timestamp;
  
  return date.toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit'
  });
}

/**
 * Fetches user information from the backend
 * @param {string} uuid - User UUID to fetch information for
 * @returns {Promise<Object>} - User information
 */
export async function fetchUserInfo(uuid) {
  if (!uuid) {
    console.error('Cannot fetch user info: No UUID provided');
    return null;
  }
  
  try {
    console.log(`Fetching user info for: ${uuid}`);
    
    // Use therapistClient to make the API call
    const response = await therapistClient.get(`/api/protected/therapist/users/${uuid}`);
    
    if (response && response.data) {
      console.log('User info fetched successfully:', response.data);
      return response.data;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error fetching user info:', error);
    return null;
  }
}

/**
 * Truncates text to a specified length
 * @param {string} text - Input text
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - Truncated text with ellipsis if needed
 */
export function truncateText(text, maxLength = 30) {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}